import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTable, useSortBy, usePagination } from 'react-table';
import { FaSyncAlt, FaDownload } from 'react-icons/fa';
import LoadingImpulse from './LoadingImpulse'; 

function Users() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const renderCount = React.useRef(0);

  useEffect(() => {
    renderCount.current += 1;
    console.log(`Render count: ${renderCount.current}`);
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching users from /api/admin/users');
      const response = await axios.get('https://sys.instant-m-transfer.com/api/admin/users');
      console.log('Users fetched:', response.data);
      const sortedUsers = (response.data.users || []).sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setUsers(sortedUsers);
      setError('');
    } catch (err) {
      const errorMessage = err.response
        ? `Failed to fetch users: ${err.response.status} - ${err.response.data?.error || 'No error message'}`
        : `Failed to fetch users: ${err.message}`;
      console.error(errorMessage);
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { Header: 'Phone', accessor: 'phone', Cell: ({ value }) => value || ' ' },
      { Header: 'Name', accessor: 'fullname' },
      { Header: 'Email', accessor: 'email' },
      { Header: 'Deriv Account', accessor: 'deriv_account' },
      { Header: 'Balance', accessor: 'balance', Cell: ({ value }) => typeof value === 'number' ? `$${value.toFixed(2)}` : 'N/A' },
      { Header: 'Created', accessor: 'createdAt', Cell: ({ value }) => new Date(value).toLocaleDateString() }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = useTable(
    { columns, data: users, initialState: { pageSize: 20 } },
    useSortBy,
    usePagination
  );

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleExportCSV = () => {
    const headers = columns.map(col => col.Header);
    const csvRows = [
      headers.join(','),
      ...users.map(row => [
        row.phone || 'N/A',
        row.fullname || 'N/A',
        row.email || 'N/A',
        row.deriv_account || 'N/A',
        typeof row.balance === 'number' ? `$${row.balance.toFixed(2)}` : 'N/A',
        row.createdAt ? new Date(row.createdAt).toLocaleDateString() : 'N/A'
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (error) return (
    <div className="bg-red-50 border-l-2 border-red-500 p-2 rounded-r shadow text-red-700 text-xs font-medium text-center mt-2 sm:p-3 sm:mt-4 sm:text-sm md:p-4 md:mt-6 md:text-base font-sfts-medium">
      {error}
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <LoadingImpulse size="lg" color="blue" />
        <style jsx global>{`
          @keyframes impulse {
            0%, 100% { transform: scaleY(0.4); opacity: 0.5; }
            50% { transform: scaleY(1); opacity: 1; }
          }
          .animate-impulse {
            animation: impulse 1.2s infinite ease-in-out;
            transform-origin: bottom;
          }
        `}</style>
      </div>
    );
  }

  if (!users.length) return (
    <div className="text-center mt-2 text-gray-500 text-xs italic sm:mt-4 sm:text-sm md:mt-6 md:text-base font-sfts-medium">
      No users found
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4">
      <div className="max-w-full mx-auto sm:max-w-3xl md:max-w-6xl">
        <div className="flex justify-between items-center mb-2 sm:mb-4 md:mb-6">
          <h2 className="text-base font-bold text-gray-800 tracking-tight sm:text-xl md:text-2xl font-sfts-title">
            Users
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              className="px-3 py-1 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 text-xs font-medium transition-all duration-200 flex items-center gap-1 sm:px-4 sm:py-2 sm:text-sm font-sfts-medium"
            >
              <FaDownload />
              Export CSV
            </button>
            <button
              onClick={handleRefresh}
              className="px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 text-xs font-medium transition-all duration-200 flex items-center gap-1 sm:px-4 sm:py-2 sm:text-sm font-sfts-medium"
            >
              <FaSyncAlt className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white p-2 rounded shadow border border-gray-200 sm:p-3 md:p-4">
          <div className="w-full overflow-hidden">
            <table {...getTableProps()} className="w-full table-fixed">
              <thead className="bg-gray-800 text-white">
                {headerGroups.map(headerGroup => {
                  const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                  return (
                    <tr key={key} {...headerGroupProps}>
                      {headerGroup.headers.map(column => {
                        const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                        return (
                          <th
                            key={columnKey}
                            {...columnProps}
                            className="p-1 text-left text-[8px] font-semibold uppercase truncate w-1/6 sm:p-2 sm:text-xs md:p-3 md:text-sm font-sfts-bold"
                          >
                            {column.render('Header')}
                            <span className="ml-1">{column.isSorted ? (column.isSortedDesc ? ' Down' : ' Up') : ''}</span>
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>

              <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
                {page.map(row => {
                  prepareRow(row);
                  const { key, ...rowProps } = row.getRowProps();
                  return (
                    <tr key={key} {...rowProps} className="hover:bg-gray-50 transition-colors duration-200">
                      {row.cells.map(cell => {
                        const { key: cellKey, ...cellProps } = cell.getCellProps();
                        return (
                          <td
                            key={cellKey}
                            {...cellProps}
                            className="p-1 text-[10px] text-gray-700 truncate w-1/6 sm:p-2 sm:text-sm md:p-3 md:text-base font-sfts"
                          >
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-2 flex flex-col sm:flex-row justify-between items-center gap-1 sm:mt-3 sm:gap-2 md:mt-4 md:gap-3">
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-medium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-sfts-medium"
          >
            Previous
          </button>

          <span className="text-xs text-gray-600 sm:text-sm md:text-base font-sfts-medium">
            Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
          </span>

          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-medium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-sfts-medium"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default Users;